/**
 * SprintRunner.jsx — owner-only GitHub issue backlog mirror.
 *
 * Read path : /api/v2/widgets/sprint-runner (owner)
 *
 * NOT a control board for tools/sprint-runner — it reads no slot state, run
 * reports, or spec. Open issues are the sprint source of truth; model tiers
 * are planning recommendations. The widget type stays 'sprint-runner' because
 * saved layouts key off it.
 */
import { Show, For, createMemo, createSignal } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import wm from '../lib/wmStore.js';
import './SprintRunner.css';

function fmtAt(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const ago = Math.round((Date.now() - d.getTime()) / 1000);
  if (ago < 60) return ago + 's ago';
  if (ago < 3600) return Math.round(ago / 60) + 'm ago';
  if (ago < 86400) return Math.round(ago / 3600) + 'h ago';
  return Math.round(ago / 86400) + 'd ago';
}

const TIER_GLYPH = { low: '○', balanced: '◐', high: '◆' };
const TIER_LABEL = { low: 'low', balanced: 'balanced', high: 'high' };

function sprintLabel(issue) {
  return issue.labels?.find(label => /^sprint-\d+$/i.test(label)) || issue.milestone || null;
}

function IssueCard(props) {
  const issue = props.issue;
  const tier = issue.modelTier || 'balanced';
  const visibleLabels = (issue.labels || [])
    .filter(label => label !== issue.priority && !issue.owners?.includes(label) && label !== sprintLabel(issue))
    .slice(0, 3);
  return (
    <article class={`v2-sr__issue v2-sr__issue--${tier}`}>
      <span class="v2-sr__issue-glyph">{TIER_GLYPH[tier] || TIER_GLYPH.balanced}</span>
      <div class="v2-sr__issue-title">{issue.title}</div>
      <a class="v2-sr__issue-number" href={issue.url} target="_blank" rel="noopener">#{issue.issue}</a>
      <div class="v2-sr__issue-meta" style={{ 'grid-column': '2 / span 2' }}>
        <span class={`v2-sr__chip v2-sr__chip--tier-${tier}`}>{TIER_LABEL[tier] || tier}</span>
        <span>{issue.owner}</span>
        <Show when={issue.priority}>
          <span class="v2-sr__chip v2-sr__chip--priority">{issue.priority}</span>
        </Show>
        <Show when={sprintLabel(issue)}>
          <span class="v2-sr__chip v2-sr__chip--sprint">{sprintLabel(issue)}</span>
        </Show>
        <Show when={issue.assignees?.length}>
          <span class="v2-sr__issue-assignees">@{issue.assignees.join(', @')}</span>
        </Show>
      </div>
      <div class="v2-sr__issue-foot" style={{ 'grid-column': '2 / span 2' }}>
        <span>updated {fmtAt(issue.updatedAt)}</span>
        <Show when={issue.comments}>
          <span>{issue.comments} comment{issue.comments === 1 ? '' : 's'}</span>
        </Show>
        <For each={visibleLabels}>{label => <span class="v2-sr__label">{label}</span>}</For>
      </div>
    </article>
  );
}

export function SprintRunnerPanel(props = {}) {
  const [refreshNonce, setRefreshNonce] = createSignal(0);
  const { data, loading, error } = useWidgetData('sprint-runner', () => ({ refresh: refreshNonce() }));
  const [tierFilter, setTierFilterRaw] = createSignal(props.settings?.tierFilter || 'all');
  function setTierFilter(next) {
    setTierFilterRaw(next);
    const wid = props.widget?.id;
    if (wid) {
      wm.setSettings(wid, { ...(props.widget?.settings || {}), tierFilter: next });
    }
  }

  const issues = createMemo(() => data()?.issues || []);
  const totals = createMemo(() => data()?.totals?.modelTiers || { low: 0, balanced: 0, high: 0 });
  const visibleIssues = createMemo(() => {
    const selected = tierFilter();
    return selected === 'all' ? issues() : issues().filter(issue => issue.modelTier === selected);
  });
  const source = createMemo(() => data()?.source || {});
  const repoIssuesUrl = createMemo(() => `https://github.com/${data()?.repo || 'EchoTiger/RedfurBot'}/issues?q=is%3Aissue%20is%3Aopen`);

  return (
    <div class={`v2-system-panel v2-system-panel--runner v2-sr ${props.class || ''}`}>
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__title">🛰️ Sprint Runner</span>
        <button class="v2-widget-hdr__btn" style={{ 'margin-left': 'auto' }}
          title="Refresh from GitHub" onClick={() => setRefreshNonce(value => value + 1)}>↻</button>
      </div>

      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <Show when={!data()?.error} fallback={<div class="v2-error">{data().error}</div>}>
            <div class="v2-widget__scroll" style={{ padding: '10px 12px' }}>

              <div class="v2-sr__hero">
                <div class="v2-sr__hero-title">
                  Open GitHub backlog · {data()?.totals?.open || 0} issues
                </div>
                <a class="v2-sr__repo-link" href={repoIssuesUrl()} target="_blank" rel="noopener">Open issues</a>
                <span class="v2-sr__pill v2-sr__pill--low">
                  ○ <b>{totals().low}</b> low
                </span>
                <span class="v2-sr__pill v2-sr__pill--balanced">
                  ◐ <b>{totals().balanced}</b> balanced
                </span>
                <span class="v2-sr__pill v2-sr__pill--high">
                  ◆ <b>{totals().high}</b> high
                </span>
                <div class="v2-sr__hero-meta" style={{ width: '100%' }}>
                  {data()?.repo} · GitHub {source().stale ? 'stale' : source().cached ? 'cached' : 'live'} · refreshed {fmtAt(source().fetchedAt)}
                </div>
              </div>

              <Show when={source().stale}>
                <div class="v2-sr__notice">
                  GitHub could not refresh this mirror. Showing the last successful issue snapshot.
                </div>
              </Show>

              <div class="v2-sr__section">
                <div class="v2-sr__section-hdr">
                  <span>Open issues</span>
                  <span class="v2-sr__section-count">{visibleIssues().length}/{issues().length}</span>
                  <div class="v2-sr__filters" role="group" aria-label="Model tier filter">
                    <For each={['all', 'low', 'balanced', 'high']}>{tier =>
                      <button classList={{ 'is-active': tierFilter() === tier }}
                        aria-pressed={tierFilter() === tier}
                        onClick={() => setTierFilter(tier)}>{tier}</button>
                    }</For>
                  </div>
                </div>
                <Show when={visibleIssues().length} fallback={
                  <div class="v2-sr__notice">No open issues match this model tier.</div>
                }>
                  <div class="v2-sr__issues">
                    <For each={visibleIssues()}>{issue => <IssueCard issue={issue} />}</For>
                  </div>
                </Show>
              </div>

              <div class="v2-sr__footer">
                <span>source: <code>GitHub open issues</code></span>
                <span>cache: <code>{source().cached ? 'shared cache' : 'fresh fetch'}</code></span>
                <Show when={source().error}>
                  <span title={source().error}>refresh error: <code>available above</code></span>
                </Show>
              </div>
            </div>
          </Show>
        </Show>
      </Show>
    </div>
  );
}

export default function SprintRunner() {
  return <SprintRunnerPanel class="v2-widget" />;
}
